import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Check, Compass, Settings2, Users } from "lucide-react";
import AppButton from "@/components/AppButtons";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { SERVICES, SITE } from "@/config/site";

const ICONS = { book: BookOpen, users: Users, settings: Settings2, compass: Compass };

export default function Services() {
  return (
    <section id="services" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>Services</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-cabinet text-4xl font-extrabold tracking-tighter sm:text-5xl">
              Complete bookkeeping &amp; payroll support.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Four areas of practice, one goal: books and payroll you never have to worry about.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const featured = s.num === "02";
            return (
              <Reveal key={s.num} delay={(i % 2) * 0.08} className="h-full">
                <article
                  data-testid={`service-card-${i}`}
                  className={`flex h-full flex-col rounded-3xl border p-8 transition-all duration-300 lg:p-10 ${
                    featured
                      ? "noise-dark border-transparent bg-brand-deep text-white shadow-ambient"
                      : "border-ink/5 bg-white shadow-subtle hover:-translate-y-1 hover:shadow-hover"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-cabinet text-sm font-bold text-accent">{s.num}</span>
                    <span
                      className={`grid h-11 w-11 place-items-center rounded-xl ${
                        featured ? "bg-white/10 text-white" : "bg-brand/5 text-brand"
                      }`}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  </div>
                  <h3 className="mt-5 font-cabinet text-2xl font-bold tracking-tight">{s.title}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-white/70" : "text-slate-600"}`}>
                    {s.blurb}
                  </p>
                  <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2 text-[13px] sm:grid-cols-2">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <Check
                          className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${featured ? "text-accent" : "text-brand"}`}
                          aria-hidden="true"
                        />
                        <span className={featured ? "text-white/80" : "text-slate-600"}>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto flex flex-wrap items-center gap-3 pt-9">
                    {s.cta.type === "contact" && (
                      <Link
                        to="/contact"
                        data-testid={`services-cta-${s.num}`}
                        className={`group inline-flex items-center gap-2 text-sm font-bold ${
                          featured ? "text-white" : "text-brand"
                        }`}
                      >
                        {s.cta.label}
                        <ArrowRight
                          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                          aria-hidden="true"
                        />
                      </Link>
                    )}
                    {s.cta.type === "postwage" && (
                      <AppButton app={SITE.apps.postWage} variant="accent" size="md" testid="services-cta-postwage" />
                    )}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
